import { DateTime } from 'luxon'

import { digObject, sortArrayBy } from '@domain-group/javascript-utils'

import useCampaigns from '@hooks/useCampaigns'
import useCurrentUser from '@hooks/useCurrentUser'

import type { CampaignModel } from '@models/campaign'

import defaultRequestOptions from '@sections/Client/packs/Overviews/defaultRequestOptions'

const today = DateTime.now().toFormat('yyyy-MM-dd')

const sortCampaignsByAuctionDate = ((campaign: CampaignModel) => {
  const auctionDate = digObject(campaign, 'dates.auction.date_time')
  return DateTime.fromFormat(auctionDate, 'dd/MM/yyyy HH:mm')
})

const groupCampaignsByDate = (campaigns: CampaignModel[]) => {
  const groupedCampaignsByDate: Record<string, { date: DateTime, items: CampaignModel[] }> = {}

  campaigns.forEach((campaign) => {
    const groupKey = digObject(campaign, 'dates.auction.date', '')
    const groupKeyDateTime = DateTime.fromFormat(groupKey, 'dd/MM/yyyy')

    const hasAuctionDate = !!groupKey

    if (hasAuctionDate){
      if (!groupedCampaignsByDate[groupKey]){
        groupedCampaignsByDate[groupKey] = {
          date: groupKeyDateTime,
          items: [],
        }
      }

      groupedCampaignsByDate[groupKey].items.push(campaign)
    }
  })

  return groupedCampaignsByDate
}

const useAuctions = (props: { agencyIdParam: string, selectedAgencyId: number }) => {
  const { agencyIdParam, selectedAgencyId } = props
  const { isAgent, isEbuAdmin } = useCurrentUser()

  const campaignsPayload = useCampaigns({
    filters: {
      auction_date_start: today,
      agency_id: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
    },
    performHttpRequests: !!agencyIdParam,
    requestOptions: {
      ...defaultRequestOptions.campaign,
    },
  })

  const { filteredCampaigns, loading } = campaignsPayload

  const sortedCampaigns = sortArrayBy(filteredCampaigns, 'asc', sortCampaignsByAuctionDate)
  const groupedCampaigns = groupCampaignsByDate(sortedCampaigns)

  const hasCampaigns = Object.keys(groupedCampaigns)?.length > 0

  return {
    groupedCampaigns,
    hasCampaigns,
    loading,
  }
}

export default useAuctions
